import { BaseResponse } from "../../../core/models/overview.model";
import { ApplicationType } from "../../../core/constants/application-type.enum";
import { PayerData } from "../../application/models/payer-data.model";

export class PaymentAppFilterValues {
  paymentTypes: PaymentType[] | null = null;
  paymentStatuses: PaymentStatus[] | null = null;
}

export class PaymentAppFilter {
  statuses: { label: string; value: PaymentStatus; checked: boolean }[] = [];

  statusCount(): number {
    return this.statuses.filter(i => i.checked).length;
  }

  /**
   * US-33: statuses are seeded from the full PaymentStatus enum rather than
   * filter.paymentStatuses, so the filter list always covers every backend status.
   */
  mapValues(filter: PaymentAppFilterValues): void {
    this.statuses = Object.values(PaymentStatus).map(item => ({
      checked: false,
      value: item,
      label: PAYMENT_STATUS_MAP.get(item) || humanizePaymentValue(item)
    }));
  }
}

export class PaymentAppRequest {
  page: number = 0;
  size: number = 10;
  paymentStatuses: string | null = null;
  dateFrom: string | null = null;
  dateTo: string | null = null;
  search: string | null = null;
  sort: string | null = 'createdAt,desc';

  params(): string {
    const p = new URLSearchParams();

    p.append('page', String(this.page));
    p.append('size', String(this.size));

    if (this.paymentStatuses) p.append('paymentStatuses', this.paymentStatuses);
    if (this.dateFrom) p.append('dateFrom', this.dateFrom);
    if (this.dateTo) p.append('dateTo', this.dateTo);
    if (this.search) p.append('search', this.search);
    if (this.sort) p.append('sort', this.sort);

    return p.toString();
  }

  mapFilter(filter: PaymentAppFilter): void {
    this.paymentStatuses = filter.statuses.filter(i => i.checked).length > 0
      ? filter.statuses.filter(i => i.checked).map(i => i.value).join(',')
      : null;
  }
}

export interface PaymentAppResponse extends BaseResponse {
    content: Payment[];
}

export interface Payment {
  id: number;
  payment?: string;
  paymentNumber?: string;
  applicationId: string;
  applicationNumber?: string;
  /** Internal application id — not yet returned by back-office-payment/list; needed to link to Application Overview. */
  applicationDbId?: number;
  /** ApplicationType (see core/constants/application-type.enum.ts) — not yet returned by back-office-payment/list. */
  applicationTypeId?: number;
  createdAt: Date;
  paymentType: PaymentType;
  invoiceNumber: number | string;
  paymentOrder?: number | string;
  epayReference?: number | string;
  paymentDate: Date;
  paymentStatus: PaymentStatus;
  payerEmail: string;
  payerType: string;
}

/**
 * "Application Type" per US-33a is the application channel (Online/Offline), not the payment
 * method — but `back-office-payment/list` doesn't return channel. api-int's
 * `ApplicationTypeEnum.getPrefix(channel)` only ever prefixes a REGISTRATION applicationId with
 * "REG-OFFLINE-" for an OFFLINE-channel application; every other applicationId (plain "REG-…" or
 * "POST-REG-…", which has no offline variant) is Online. That prefix is already in `applicationId`
 * today, so channel can be read off it without a backend change.
 */
export function getApplicationTypeText(applicationId: string | null | undefined): string {
  return applicationId?.startsWith('REG-OFFLINE-') ? 'Offline' : 'Online';
}

/** DR-832 / US-33a: read-only "Payment details" side panel opened from a payments-list row. */
export interface PaymentParty {
  isBodyCorporate?: boolean;
  /** Person name (individual) or company name (legal entity). */
  name?: string;
  /** Individual, resident. */
  iin?: string;
  /** Legal entity, resident. */
  bin?: string;
  /** Non-resident: country of citizenship (individual) or country of incorporation (legal entity). */
  country?: string;
  address?: string;
  email?: string;
}

export enum PaymentErrorSeverity {
  BLOCKING = 'BLOCKING',
  WARNING = 'WARNING',
  RESOLVED = 'RESOLVED',
}

/**
 * Resolved errors stay in the history forever (audit trail) even once the status has passed —
 * do not filter them out once `status` is no longer current.
 */
export interface PaymentStatusError {
  status: PaymentStatus;
  message: string;
  occurredAt: Date | string;
  severity: PaymentErrorSeverity;
}

export interface PaymentStatusHistoryItem {
  status: PaymentStatus;
  timestamp?: Date | string | null;
}

export interface PaymentDocument {
  code: 'PAYMENT_INVOICE' | 'ACT_OF_COMPLETION';
  name: string;
  /** > 1 renders as a suffix, e.g. "Act of Completion v2 (signed)". */
  documentVersion?: number;
  available: boolean;
  /** `document/download/{id}` param — the same generic download endpoint used elsewhere in front-int. */
  id?: number;
}

/**
 * US-33a: full detail payload for `back-office-payment/{applicationId}`. Some fields (documentVersion,
 * statusHistory/statusErrors beyond the static "Sending to 1C" flow per DR-832) aren't sent by the
 * backend yet and stay optional/placeholder until it does.
 */
export interface PaymentDetails {
  id: number;
  applicationId?: string;
  applicationNumber?: string;
  applicationDbId?: number;
  applicationTypeId?: number;
  /** Application channel — PORTAL (customer) or OFFLINE (case officer), not the payment type. Displayed as "Online"/"Offline". */
  applicationChannel?: string;
  applicationStatus?: string;
  service?: string;
  payer?: PaymentParty;
  biller?: PaymentParty;
  paymentOrder?: number | string;
  invoiceNumber?: number | string;
  amount?: number | string;
  currency?: string;
  cardMask?: string;
  ips?: string;
  issuer?: string;
  rrn?: string;
  submissionDate?: Date | string;
  paymentDate?: Date | string;
  statusHistory?: PaymentStatusHistoryItem[];
  statusErrors?: PaymentStatusError[];
  documents?: PaymentDocument[];
}

/**
 * US-33: full 1C -> Act of Completion -> signature pipeline. Matches api-int's PaymentStatus.java
 * exactly — DONE is the single terminal status (signed or auto-closed act sent back to 1C).
 */
export enum PaymentStatus {
  PAID = 'PAID',
  PROCESSING_1C = 'PROCESSING_1C',
  PAYMENT_CONFIRMED = 'PAYMENT_CONFIRMED',
  GENERATING_ACT = 'GENERATING_ACT',
  ACT_RECEIVED = 'ACT_RECEIVED',
  AWAITING_SIGNATURE = 'AWAITING_SIGNATURE',
  SIGNED = 'SIGNED',
  DONE = 'DONE',
}

export enum PaymentType {
  ONLINE_PAYMENT = 'ONLINE_PAYMENT',
  OFFLINE_PAYMENT = 'OFFLINE_PAYMENT',
}

export const PAYMENT_STATUS_MAP: Map<string, string> = new Map([
  [PaymentStatus.PAID, 'Paid'],
  [PaymentStatus.PROCESSING_1C, 'Processing in 1C'],
  [PaymentStatus.PAYMENT_CONFIRMED, 'Payment confirmed'],
  [PaymentStatus.GENERATING_ACT, 'Generating Act of Completion'],
  [PaymentStatus.ACT_RECEIVED, 'Act received'],
  [PaymentStatus.AWAITING_SIGNATURE, 'Awaiting signature'],
  [PaymentStatus.SIGNED, 'Signed'],
  [PaymentStatus.DONE, 'Done'],
]);

export function humanizePaymentValue(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Full 8-step visual flow (US-33a), matching api-int's PaymentStatus order exactly. */
export const PAYMENT_STATUS_FLOW: PaymentStatus[] = [
  PaymentStatus.PAID,
  PaymentStatus.PROCESSING_1C,
  PaymentStatus.PAYMENT_CONFIRMED,
  PaymentStatus.GENERATING_ACT,
  PaymentStatus.ACT_RECEIVED,
  PaymentStatus.AWAITING_SIGNATURE,
  PaymentStatus.SIGNED,
  PaymentStatus.DONE,
];

/** Shared ng-zorro tag colour for a payment status — used by both the list table and the details drawer. */
export function getPaymentStatusColor(status: PaymentStatus | string): string {
  switch (status) {
    case PaymentStatus.PAID:
      return 'green';
    case PaymentStatus.PROCESSING_1C:
    case PaymentStatus.GENERATING_ACT:
      return 'blue';
    case PaymentStatus.PAYMENT_CONFIRMED:
    case PaymentStatus.ACT_RECEIVED:
      return 'cyan';
    case PaymentStatus.AWAITING_SIGNATURE:
      return 'gold';
    case PaymentStatus.SIGNED:
    case PaymentStatus.DONE:
      return 'green';
    default:
      return 'default';
  }
}

/** Shared Application Overview link — used by both the list table and the details drawer. */
export function getPaymentApplicationLink(item: Payment): any[] {
  const base = item.applicationTypeId === ApplicationType.POST_REGISTRATION
    ? '/applications/post-registration/edit'
    : '/applications/registration/edit';
  return [base, item.applicationDbId];
}

/** Raw shape of `GET back-office-payment/{applicationId}` (DR-832 / US-33a). */
export interface BackOfficePaymentResponse {
  id: number;
  applicationId: string;
  createdAt?: string;
  updatedAt?: string;
  paymentType: PaymentType | string;
  invoiceNumber: number | string;
  epayReference?: number | string;
  paymentDate?: string;
  paymentStatus: PaymentStatus | string;
  payerEmail?: string;
  payerType?: string;
  amount?: number;
  currency?: string;
  billingDetails?: {
    payerData?: PayerData;
    billerData?: PayerData;
    invoiceLines?: { description?: string }[];
    transactionDetails?: {
      cardMask?: string;
      /** Ticket names this field "Payment IPS" on the front end, despite the backend key. */
      cardType?: string;
      issuer?: string;
      reference?: string;
    };
  };
  /** `channel` is either PORTAL or OFFLINE (never "ONLINE") — see applicationChannelText. */
  applicationInfo?: {
    backOfficeApplicationId?: number;
    status?: string;
    channel?: string;
    applicationSubmissionDate?: string;
  };
  documents?: { id: number; fileName: string; contentType?: string; code: PaymentDocument['code'] }[];
}

const PAYMENT_DOCUMENT_LABELS: Record<PaymentDocument['code'], string> = {
  PAYMENT_INVOICE: 'Payment Invoice',
  ACT_OF_COMPLETION: 'Act of Completion',
};

function toPaymentParty(data: PayerData | null | undefined, countryName: (id: number) => string | undefined): PaymentParty | undefined {
  if (!data) {
    return undefined;
  }

  const countryId = data.isBodyCorporate ? data.incorporationCountryId : data.citizenshipCountryId;
  const address = [data.addressStreet, data.addressBuilding, data.addressOffice, data.addressCity, data.addressPostalCode]
    .filter(Boolean)
    .join(', ');

  return {
    isBodyCorporate: data.isBodyCorporate,
    name: data.isBodyCorporate
      ? (data.companyName || undefined)
      : ([data.firstName, data.patronymicName, data.lastName].filter(Boolean).join(' ') || undefined),
    iin: !data.isBodyCorporate ? (data.iin || undefined) : undefined,
    bin: data.isBodyCorporate ? (data.registrationNumber || undefined) : undefined,
    country: countryId ? countryName(countryId) : undefined,
    address: address || undefined,
    email: data.email || undefined,
  };
}

/**
 * `documents` only lists what the backend has actually generated so far (e.g. the invoice exists
 * immediately, the Act of Completion only once 1C finishes) — always render both known slots so
 * the not-yet-generated one still shows as unavailable rather than disappearing.
 */
function toPaymentDocuments(raw: BackOfficePaymentResponse['documents']): PaymentDocument[] {
  const byCode = new Map((raw || []).map(doc => [doc.code, doc]));

  return (Object.keys(PAYMENT_DOCUMENT_LABELS) as PaymentDocument['code'][]).map(code => {
    const doc = byCode.get(code);
    return { code, name: PAYMENT_DOCUMENT_LABELS[code], available: !!doc, id: doc?.id };
  });
}

/** Maps the raw `back-office-payment/{applicationId}` response onto the drawer's display model. */
export function mapPaymentDetails(raw: BackOfficePaymentResponse, countryName: (id: number) => string | undefined): PaymentDetails {
  const billing = raw.billingDetails;
  const service = (billing?.invoiceLines || []).map(line => line.description).filter(Boolean).join(', ');

  return {
    id: raw.id,
    applicationId: raw.applicationId,
    applicationDbId: raw.applicationInfo?.backOfficeApplicationId,
    applicationChannel: raw.applicationInfo?.channel,
    applicationStatus: raw.applicationInfo?.status,
    service: service || undefined,
    payer: toPaymentParty(billing?.payerData, countryName),
    biller: toPaymentParty(billing?.billerData, countryName),
    paymentOrder: raw.epayReference,
    invoiceNumber: raw.invoiceNumber,
    amount: raw.amount,
    currency: raw.currency,
    cardMask: billing?.transactionDetails?.cardMask,
    ips: billing?.transactionDetails?.cardType,
    issuer: billing?.transactionDetails?.issuer,
    rrn: billing?.transactionDetails?.reference,
    submissionDate: raw.applicationInfo?.applicationSubmissionDate,
    paymentDate: raw.paymentDate,
    documents: toPaymentDocuments(raw.documents),
  };
}